import {
  Circle,
  CheckCircle2,
  Loader,
  XCircle,
  FileText,
  FileSpreadsheet,
  FileImage,
  Presentation,
  FileType2,
  Tag,
  FolderOpen,
} from "lucide-react";
import type { FilterCategoryGroup } from "@/components/ui";
import type { Tag as TagType } from "@/components/ui/Tag/types";
import type { DocumentCategoryView } from "@/packages/types";

/**
 * Document-specific filter categories for the Filter component.
 *
 * Status values map to `summaryStatus` backend field via mapStatus() in types.ts:
 *   - "draft"        → summaryStatus is null/undefined (no summary)
 *   - "final"        → summaryStatus === "completed"
 *   - "under-review" → summaryStatus === "generating" | "retrying"
 *   - "expired"      → summaryStatus === "failed"
 *
 * File Type values map to the `fileType` backend field directly.
 * Tag values are dynamically populated from the database via getDocumentFilterCategories().
 */

const BASE_CATEGORIES: FilterCategoryGroup[] = [
  {
    label: "Document Properties",
    categories: [
      {
        id: "status",
        label: "Status",
        icon: Circle,
        allowedOperators: ["is_either_of", "is_not"],
        values: [
          { id: "draft", label: "Draft", icon: Circle, color: "#6B6B6B" },
          {
            id: "under-review",
            label: "Under Review",
            icon: Loader,
            color: "#E8862A",
          },
          {
            id: "final",
            label: "Final",
            icon: CheckCircle2,
            color: "#4CAF50",
          },
          { id: "expired", label: "Expired", icon: XCircle, color: "#9B9B9B" },
        ],
      },
      {
        id: "file_type",
        label: "File Type",
        icon: FileText,
        allowedOperators: ["is", "is_not", "is_either_of"],
        values: [
          { id: "pdf", label: "PDF", icon: FileText, color: "#E53935" },
          { id: "docx", label: "DOCX", icon: FileType2, color: "#2196F3" },
          {
            id: "xlsx",
            label: "XLSX",
            icon: FileSpreadsheet,
            color: "#4CAF50",
          },
          {
            id: "pptx",
            label: "PPTX",
            icon: Presentation,
            color: "#FF9800",
          },
          { id: "txt", label: "TXT", icon: FileType2, color: "#9E9E9E" },
          { id: "image", label: "Image", icon: FileImage, color: "#AB47BC" },
        ],
      },
      {
        id: "tags",
        label: "Tags",
        icon: Tag,
        allowedOperators: ["includes_any", "includes_all", "includes_none"],
        values: [],
      },
      {
        id: "category",
        label: "Category",
        icon: FolderOpen,
        allowedOperators: ["is", "is_not", "is_either_of"],
        values: [],
      },
    ],
  },
];

export function getDocumentFilterCategories(
  tags: TagType[],
  categories: DocumentCategoryView[],
): FilterCategoryGroup[] {
  const filterGroups = BASE_CATEGORIES.map((group) => {
    if (group.categories) {
      return {
        ...group,
        categories: group.categories.map((category) => {
          if (category.id === "tags") {
            return {
              ...category,
              values: tags.map((tag) => ({
                id: tag.id,
                label: tag.name,
                color: tag.color,
              })),
            };
          }
          if (category.id === "category") {
            return {
              ...category,
              values: categories.map((cat) => ({
                id: cat._id,
                label: cat.name,
                // Icon is stored as string in DB, not LucideIcon component
              })),
            };
          }
          return category;
        }),
      };
    }
    return group;
  });
  return filterGroups;
}
